/**
 * Bond Series Factory - Creates BondSeries objects from minimal user input
 *
 * Converts quick input (starting tax year, duration, optional target/purpose)
 * into fully-populated BondSeries objects with Michigan school bond defaults.
 */

import type { ProjectData } from '~/hooks/useProjectStore';
import type { QuickBondInput } from '~/pages/project/ProposalDevelopmentPage/AddBondModal';
import type { BondSeries, Project } from '~/types';
import { TargetType } from '~/types';
import type { BondInterestRateSettings } from '~/types/app-settings';
import { DEFAULT_APP_SETTINGS } from '~/types/app-settings';
import type { BondConfiguration } from '~/types/bond-inputs';

export interface BondCreationOptions {
  quickInput: QuickBondInput;
  project: ProjectData;
}

/**
 * Creates a BondSeries object from minimal quick input
 * Uses sensible defaults for fields not specified by user
 */
export function createBondFromQuickInput(options: BondCreationOptions): BondSeries {
  const { quickInput, project } = options;

  // Validate project has required date elements
  // Check both dateRecords (direct) and dateElements.dateRecords
  const dateRecords = project.dateRecords || project.dateElements?.dateRecords;
  if (!dateRecords || dateRecords.length === 0) {
    throw new Error('Project must have date records initialized before adding bonds');
  }

  // Calculate payment dates from starting tax year + duration
  const { firstPrincipalPaymentDate, finalPrincipalPaymentDate } = calculatePaymentDates(
    quickInput.startingTaxYear,
    quickInput.term,
    project
  );

  // Use tax year as the bond name (primary identifier)
  const bondName = String(quickInput.startingTaxYear);

  // Get the starting tax year's date record to determine payment schedule
  const startingYearRecord = dateRecords.find((dr) => dr.taxYear === quickInput.startingTaxYear);
  if (!startingYearRecord) {
    throw new Error(`No date record found for tax year ${quickInput.startingTaxYear}`);
  }

  // Determine payment months from actual payment dates
  // Extract unique months from payment dates
  const paymentMonths = new Set<number>();
  startingYearRecord.paymentDates.forEach((dateStr) => {
    const date = new Date(dateStr);
    paymentMonths.add(date.getMonth() + 1); // Convert to 1-based month (1-12)
  });
  const interestPaymentMonths = Array.from(paymentMonths).sort((a, b) => a - b);

  // Principal payment month is the month of the last payment date of fiscal year
  const firstPrincipalDate = new Date(firstPrincipalPaymentDate);
  const principalPaymentMonths = [firstPrincipalDate.getMonth() + 1]; // Convert to 1-based

  // First interest payment: First payment date in the starting tax year
  // (For summer: November, for winter: May of next year)
  const firstInterestPaymentDate = startingYearRecord.paymentDates[0] || firstPrincipalPaymentDate;

  // Issue date: Use fiscal year end date from previous tax year
  // The issue date is the fiscal year end of the tax year BEFORE the starting tax year
  // For summer: Tax year 2024's fiscal year ends 2025-06-30, so issue date for 2025 bond is 2025-06-30
  // For winter: Tax year 2024's fiscal year ends 2025-11-01, so issue date for 2025 bond is 2025-11-01
  const previousTaxYear = quickInput.startingTaxYear - 1;
  const previousYearRecord = dateRecords.find((dr) => dr.taxYear === previousTaxYear);

  let issueDate: string;
  if (previousYearRecord?.fiscalYearEndDate) {
    // Use the fiscal year end date from the previous tax year
    issueDate = previousYearRecord.fiscalYearEndDate;
  } else {
    // Calculate from starting tax year's fiscal year end by subtracting one year
    // Extract year from fiscal year end and subtract 1
    const fiscalYearEndDate = new Date(startingYearRecord.fiscalYearEndDate);
    const issueDateObj = new Date(fiscalYearEndDate);
    issueDateObj.setFullYear(fiscalYearEndDate.getFullYear() - 1);
    issueDate = issueDateObj.toISOString().split('T')[0];
  }

  // Interest rate structure: Use project-level settings or defaults
  // QuickInput doesn't include interest rates - they're always auto-calculated
  const projectInterestRates: BondInterestRateSettings =
    project.bondInterestRateSettings || DEFAULT_APP_SETTINGS.bondInterestRates;

  // Issue date is always calculated from tax year
  const finalIssueDate = issueDate;

  // Store escalation info in bond for later use
  interface InterestRateEscalation {
    startRate: number;
    incrementRate: number;
    maxRate: number;
  }

  const bondSeriesWithEscalation: BondSeries & {
    __interestRateEscalation?: InterestRateEscalation;
  } = {
    id: `bond-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
    name: bondName,
    issueDate: finalIssueDate,
    interestPaymentMonths,
    principalPaymentMonths,
    firstInterestPaymentDate,
    firstPrincipalPaymentDate,
    finalPrincipalPaymentDate,
    // Payment map will be built by BondSeriesBuilder.initializeBondSeries()
    bondPaymentMap: {},
    // Target information (optional)
    targetInformation: quickInput.targetAmount
      ? {
          principalTargetAmount: quickInput.targetAmount,
          targetType: TargetType.DOES_NOT_EXCEED,
        }
      : undefined,
    // Store escalation info in bond for later use (internal field)
    __interestRateEscalation: {
      startRate: projectInterestRates.startRate / 100,
      incrementRate: projectInterestRates.incrementRate / 100,
      maxRate: projectInterestRates.maxRate / 100,
    },
  };

  return bondSeriesWithEscalation;
}

/**
 * Calculate payment dates from starting tax year and duration
 * Uses actual payment dates from project date records
 * Principal payments use the last payment date of each fiscal year
 */
function calculatePaymentDates(
  startingTaxYear: number,
  durationYears: number,
  project: ProjectData
): {
  firstPrincipalPaymentDate: string;
  finalPrincipalPaymentDate: string;
} {
  // Use dateRecords from either location
  const dateRecords = project.dateRecords || project.dateElements?.dateRecords;
  if (!dateRecords || dateRecords.length === 0) {
    throw new Error('Project must have date records initialized before adding bonds');
  }

  // Find date record for the starting tax year
  const startingYearRecord = dateRecords.find((dr) => dr.taxYear === startingTaxYear);

  if (!startingYearRecord) {
    throw new Error(`No date records found for tax year ${startingTaxYear}`);
  }

  // First principal payment: Use the last payment date of the fiscal year for the starting tax year
  // This is the principal payment date (May for summer, November for winter)
  const firstPrincipalPaymentDate = startingYearRecord.lastPaymentDateOfFiscalYear;

  if (!firstPrincipalPaymentDate) {
    throw new Error(`No last payment date found for tax year ${startingTaxYear}`);
  }

  // Final principal payment: Last payment date in (startingTaxYear + durationYears - 1)
  const finalTaxYear = startingTaxYear + durationYears - 1;
  const finalYearRecord = dateRecords.find((dr) => dr.taxYear === finalTaxYear);

  if (!finalYearRecord) {
    throw new Error(`No date records found for final tax year ${finalTaxYear}`);
  }

  const finalPrincipalPaymentDate = finalYearRecord.lastPaymentDateOfFiscalYear;

  if (!finalPrincipalPaymentDate) {
    throw new Error(`No last payment date found for final tax year ${finalTaxYear}`);
  }

  return {
    firstPrincipalPaymentDate,
    finalPrincipalPaymentDate,
  };
}

/**
 * Create BondSeries from full bond configuration
 * Uses tax year to align with project date structure
 * Applies escalating interest rates correctly
 */
export interface CreateBondFromConfigurationParams {
  configuration: BondConfiguration;
  project: Project;
}

export function createBondFromConfiguration({
  configuration,
  project,
}: CreateBondFromConfigurationParams): BondSeries {
  const { name, issueDate, taxYear, durationYears, targetPrincipal, interestRateSettings } =
    configuration;

  // Get interest rate settings (bond override → project → fallback)
  const interestRates: BondInterestRateSettings = interestRateSettings ||
    project.bondInterestRateSettings || { startRate: 3.0, incrementRate: 0.05, maxRate: 5.0 };

  // Find first payment date from project dates matching tax year
  // Tax year dictates the starting interest date and first payment date
  const dateRecords = project.dateElements?.dateRecords || [];
  const matchingRecord = dateRecords.find((record) => record.taxYear === taxYear);

  if (!matchingRecord) {
    throw new Error(
      `Cannot create bond: No date record found for tax year ${taxYear}. ` +
        `Ensure project dates are built before adding bonds.`
    );
  }

  const firstPrincipalPaymentDate = matchingRecord.lastPaymentDateOfFiscalYear;

  // Calculate final payment date (duration years from first payment)
  const firstDate = new Date(firstPrincipalPaymentDate);
  const finalDate = new Date(firstDate);
  finalDate.setFullYear(finalDate.getFullYear() + durationYears);
  const finalPrincipalPaymentDate = finalDate.toISOString().split('T')[0];

  // Determine payment months from actual payment dates
  const paymentMonths = new Set<number>();
  matchingRecord.paymentDates.forEach((dateStr) => {
    const date = new Date(dateStr);
    paymentMonths.add(date.getMonth() + 1); // Convert to 1-based month (1-12)
  });
  const interestPaymentMonths = Array.from(paymentMonths).sort((a, b) => a - b);

  // Principal payment month is the month of the first principal payment date
  const firstPrincipalDate = new Date(firstPrincipalPaymentDate);
  const principalPaymentMonths = [firstPrincipalDate.getMonth() + 1]; // Convert to 1-based

  // First interest payment: First payment date in the tax year
  const firstInterestPaymentDate = matchingRecord.paymentDates[0] || firstPrincipalPaymentDate;

  // Create BondSeries object
  interface InterestRateEscalation {
    startRate: number;
    incrementRate: number;
    maxRate: number;
  }

  const bondSeries: BondSeries & {
    __interestRateEscalation?: InterestRateEscalation;
    bondInterestRateSettings?: BondInterestRateSettings;
  } = {
    id: `bond-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    name,
    issueDate,
    interestPaymentMonths,
    principalPaymentMonths,
    firstInterestPaymentDate,
    firstPrincipalPaymentDate,
    finalPrincipalPaymentDate,
    // Payment map will be built by BondSeriesBuilder.initializeBondSeries()
    bondPaymentMap: {},
    // Target information (optional)
    targetInformation: targetPrincipal
      ? {
          principalTargetAmount: targetPrincipal,
          targetType: TargetType.DOES_NOT_EXCEED,
        }
      : undefined,
    // Store escalation info in bond for later use (internal field)
    __interestRateEscalation: {
      startRate: interestRates.startRate / 100,
      incrementRate: interestRates.incrementRate / 100,
      maxRate: interestRates.maxRate / 100,
    },
    // Store bond-level override for persistence (only if explicitly overridden)
    bondInterestRateSettings: interestRateSettings ? interestRates : undefined,
  };

  return bondSeries;
}

/**
 * Update an existing BondSeries with new configuration values
 * Preserves the bond ID and updates all other fields based on configuration
 */
export interface UpdateBondFromConfigurationParams {
  existingBond: BondSeries;
  configuration: BondConfiguration;
  project: Project;
}

export function updateBondFromConfiguration({
  existingBond,
  configuration,
  project,
}: UpdateBondFromConfigurationParams): BondSeries {
  const { name, issueDate, taxYear, durationYears, targetPrincipal, interestRateSettings } =
    configuration;

  // Get interest rate settings (bond override → project → fallback)
  const interestRates: BondInterestRateSettings = interestRateSettings ||
    project.bondInterestRateSettings || { startRate: 3.0, incrementRate: 0.05, maxRate: 5.0 };

  // Find date record for the target tax year
  const dateRecords = project.dateElements?.dateRecords || [];
  const matchingRecord = dateRecords.find((record) => record.taxYear === taxYear);

  if (!matchingRecord) {
    throw new Error(
      `Cannot update bond: No date record found for tax year ${taxYear}. ` +
        `Ensure project dates are built before updating bonds.`
    );
  }

  const firstPrincipalPaymentDate = matchingRecord.lastPaymentDateOfFiscalYear;

  // Calculate final payment date based on duration
  const finalTaxYear = taxYear + durationYears - 1;
  const finalRecord = dateRecords.find((record) => record.taxYear === finalTaxYear);
  const finalPrincipalPaymentDate =
    finalRecord?.lastPaymentDateOfFiscalYear || matchingRecord.lastPaymentDateOfFiscalYear;

  // Create updated BondSeries preserving the ID
  interface InterestRateEscalation {
    startRate: number;
    incrementRate: number;
    maxRate: number;
  }

  const updatedBond: BondSeries & {
    __interestRateEscalation?: InterestRateEscalation;
    bondInterestRateSettings?: BondInterestRateSettings;
  } = {
    ...existingBond,
    name,
    issueDate,
    firstPrincipalPaymentDate,
    finalPrincipalPaymentDate,
    // Target information (optional)
    targetInformation: targetPrincipal
      ? {
          principalTargetAmount: targetPrincipal,
          targetType: existingBond.targetInformation?.targetType || TargetType.DOES_NOT_EXCEED,
        }
      : undefined,
    // Store escalation info in bond for later use (internal field)
    __interestRateEscalation: {
      startRate: interestRates.startRate / 100,
      incrementRate: interestRates.incrementRate / 100,
      maxRate: interestRates.maxRate / 100,
    },
    // Store bond-level override for persistence (only if explicitly overridden)
    bondInterestRateSettings: interestRateSettings ? interestRates : undefined,
  };

  return updatedBond;
}
